<template>
  <div class="ampere-force-viz-3d">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>F = BIL sinθ$</div>
    </div>

    <div ref="canvasContainer" class="canvas-3d-box">
      <!-- Three.js Canvas -->
      <div class="hud-labels">
        <div class="hud-item magnetic">
          <span class="label">磁感应强度 B</span>
          <span class="value">0.45 T</span>
        </div>
        <div class="hud-item force">
          <span class="label">安培力 F</span>
          <span class="value">{{ (Math.abs(current) * 0.2).toFixed(2) }} N</span>
        </div>
      </div>
    </div>

    <div class="controls-panel">
      <div class="control-grid">
        <div class="control-item">
          <label>电流 I: {{ current }} A</label>
          <el-slider v-model="current" :min="-100" :max="100" size="mini"></el-slider>
        </div>
        <div class="control-item">
          <label>导线角度: {{ wireAngle }}°</label>
          <el-slider v-model="wireAngle" :min="0" :max="180" size="mini"></el-slider>
        </div>
      </div>
      <div class="rule-box">
        <div class="rule-title"><i class="el-icon-guide"></i> 左手定则助手</div>
        <p>使磁感线穿过手心，四指指向电流 <strong>I</strong>，拇指所指即为 <strong>F</strong> 方向。</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'AmpereForceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      current: 50,
      wireAngle: 90,
      running: true
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  watch: {
    current() { this.updateVectors(); },
    wireAngle() { this.updateVectors(); }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(60, 40, 60);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Lights
      const ambient = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambient);
      const directional = new THREE.DirectionalLight(0xffffff, 0.8);
      directional.position.set(10, 20, 10);
      this.scene.add(directional);

      // Magnets (N and S poles)
      const poleGeo = new THREE.BoxGeometry(30, 8, 30);
      
      const nMat = new THREE.MeshPhongMaterial({ color: 0xff4757, emissive: 0xaa0000, emissiveIntensity: 0.2 });
      this.nPole = new THREE.Mesh(poleGeo, nMat);
      this.nPole.position.y = 20;
      this.scene.add(this.nPole);

      const sMat = new THREE.MeshPhongMaterial({ color: 0x2f3542, emissive: 0x111111, emissiveIntensity: 0.2 });
      this.sPole = new THREE.Mesh(poleGeo, sMat);
      this.sPole.position.y = -20;
      this.scene.add(this.sPole);

      // Magnetic Field Lines
      this.fieldLines = new THREE.Group();
      for (let x = -10; x <= 10; x += 10) {
        for (let z = -10; z <= 10; z += 10) {
           const lineGeo = new THREE.BufferGeometry().setFromPoints([
             new THREE.Vector3(x, 20, z),
             new THREE.Vector3(x, -20, z)
           ]);
           const lineMat = new THREE.LineDashedMaterial({ color: 0xff4757, dashSize: 2, gapSize: 2, transparent: true, opacity: 0.3 });
           const line = new THREE.Line(lineGeo, lineMat);
           line.computeLineDistances();
           this.fieldLines.add(line);
        }
      }
      this.scene.add(this.fieldLines);

      // Wire
      const wireGeo = new THREE.CylinderGeometry(1, 1, 60, 16);
      const wireMat = new THREE.MeshPhongMaterial({ color: 0x00d2ff, emissive: 0x006efd, emissiveIntensity: 0.5 });
      this.wire = new THREE.Mesh(wireGeo, wireMat);
      this.wire.rotation.z = Math.PI / 2;
      this.scene.add(this.wire);

      // Current Flowing Animation
      this.electronPath = new THREE.Group();
      this.scene.add(this.electronPath);
      for(let i=0; i<10; i++) {
          const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 8), new THREE.MeshBasicMaterial({color: 0xffffff}));
          this.electronPath.add(sphere);
      }

      // Force Vector Arrow
      this.forceArrow = new THREE.ArrowHelper(
        new THREE.Vector3(1, 0, 0),
        new THREE.Vector3(0, 0, 0),
        20,
        0xffa502,
        6,
        3
      );
      this.scene.add(this.forceArrow);

      this.animate();
    },
    updateVectors() {
      // Rotate wire
      const rad = (this.wireAngle - 90) * Math.PI / 180;
      this.wire.rotation.y = rad;
      this.electronPath.rotation.y = rad;

      // Force calculation: F = BIL sin(theta)
      // I is current, B is down (-y), L is wire
      // Direction: I x B (Right hand for vector product, but we use physics' left hand)
      // Current vector in local coordinates is along wire axis (x in local)
      // Actually let's just use the logic:
      // If current > 0 and wire parallel to X, B is -Y, then F should be +Z
      const fMagnitude = (Math.abs(this.current) / 5) * Math.sin(this.wireAngle * Math.PI / 180);
      const fDir = this.current >= 0 ? 1 : -1;
      
      this.forceArrow.setLength(Math.max(0.1, fMagnitude * 2), 6, 3);
      this.forceArrow.setDirection(new THREE.Vector3(0, 0, fDir));
      this.forceArrow.visible = Math.abs(this.current) > 5;
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);

      const time = Date.now() * 0.001;
      
      // Animate electrons
      const speed = this.current * 0.1;
      this.electronPath.children.forEach((e, i) => {
          e.position.x = ((i * 6 + time * speed * 10) % 60) - 30;
          e.visible = Math.abs(this.current) > 2;
      });

      // Subtle scene rotation
      this.scene.rotation.y += 0.002;

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.ampere-force-viz-3d {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.canvas-3d-box {
  flex: 1;
  min-height: 400px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;

  .hud-labels {
    position: absolute;
    top: 24px; left: 24px;
    display: flex; flex-direction: column; gap: 12px;
    
    .hud-item {
      background: rgba(10, 10, 20, 0.6);
      backdrop-filter: blur(5px);
      padding: 10px 16px;
      border-radius: 10px;
      border-left: 4px solid transparent;
      
      &.magnetic { border-left-color: #ff4757; }
      &.force { border-left-color: #ffa502; }

      .label { display: block; font-size: 0.65rem; color: #888; text-transform: uppercase; letter-spacing: 1px; }
      .value { font-size: 1.1rem; color: #fff; font-weight: 300; font-family: monospace; }
    }
  }
}

.controls-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .control-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .control-item {
    background: rgba(255, 255, 255, 0.03);
    padding: 16px;
    border-radius: 12px;
    label { display: block; font-size: 0.85rem; color: #ccc; margin-bottom: 12px; }
  }

  .rule-box {
    background: linear-gradient(90deg, rgba(255, 165, 2, 0.1), transparent);
    border-left: 4px solid #ffa502;
    padding: 15px 20px;
    border-radius: 0 12px 12px 0;
    
    .rule-title { font-weight: bold; color: #ffa502; margin-bottom: 5px; font-size: 0.9rem; }
    p { margin: 0; font-size: 0.85rem; color: #aaa; strong { color: #fff; } }
  }
}

.viz-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .title { font-size: 1.2rem; font-weight: 300; color: #fff; }
  .formula-chip {
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    font-family: serif;
    font-style: italic;
    color: #00d2ff;
  }
}
</style>
